import { useUserInfo } from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { useAuthToken } from "../lib/auth";

const EARN_EMOJIS = ["💳", "⭐", "📷", "👥"] as const;

export function PointsPage() {
  const { t } = useTranslation("points");
  const authToken = useAuthToken();
  const { data: user } = useUserInfo(authToken);
  const points = (user?.score as number | undefined) ?? 0;
  const spentNum = (user?.amount as number | undefined) ?? 0;

  const currentVip = spentNum >= 150_000 ? 3 : spentNum >= 50_000 ? 2 : 1;
  const nextVip = currentVip >= 3 ? 3 : currentVip + 1;
  const nextThreshold = currentVip >= 3 ? 150_000 : currentVip === 2 ? 150_000 : 50_000;
  const progressPct = Math.min(100, spentNum > 0 ? (spentNum / nextThreshold) * 100 : 0);
  const remain = nextThreshold - spentNum;

  return (
    <TgPage tabbar={false}>
      <TgHeader title={t("breadcrumb.points")} back />

      <div
        style={{
          background: "linear-gradient(135deg,#FFD23F,#FFA200 60%,#FF6B4A)",
          color: "#fff",
          padding: "24px 18px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -30,
            right: -30,
            width: 140,
            height: 140,
            background: "rgba(255,255,255,.15)",
            borderRadius: "50%",
          }}
        />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, opacity: 0.8 }}>
            {t("head.label")}
          </div>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 50,
              fontWeight: 900,
              marginTop: 4,
              letterSpacing: "-0.025em",
              lineHeight: 1,
            }}
          >
            {points.toLocaleString()}
            <span style={{ fontSize: 20, marginLeft: 6 }}>★</span>
          </div>
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 6 }}>
            {t("head.equals", { points: points.toLocaleString() })}
          </div>
        </div>
      </div>

      <div className="tg-body" style={{ flex: 1 }}>
        <div className="tg-card">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <strong style={{ fontSize: 12 }}>
              {t("vip.title", { level: currentVip, spent: formatPrice(spentNum, "ru-RU", "RUB") })}
            </strong>
            <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>
              {t("vip.next", { next: nextVip, remain: formatPrice(remain, "ru-RU", "RUB") })}
            </span>
          </div>
          <div
            style={{
              height: 8,
              background: "var(--color-surface)",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${progressPct}%`,
                height: "100%",
                background: "linear-gradient(90deg,#FFD23F,#FFA200)",
                borderRadius: 4,
              }}
            />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              fontSize: 9,
              color: "var(--color-text-muted)",
              marginTop: 6,
              fontFamily: "var(--font-mono)",
            }}
          >
            {t("vip.levels")
              .split(",")
              .map((l, i) => (
                <span
                  key={i}
                  style={
                    l.includes("▲") ? { color: "var(--color-cta)", fontWeight: 800 } : undefined
                  }
                >
                  {l}
                </span>
              ))}
          </div>
        </div>

        <div className="tg-card">
          <h4 className="tg-card__h">{t("earn.title")}</h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {(["s1", "s2", "s3", "s4"] as const).map((s, i) => (
              <div
                key={s}
                style={{
                  background: "var(--color-surface)",
                  borderRadius: 8,
                  padding: 10,
                  display: "flex",
                  gap: 8,
                  fontSize: 10,
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: 18 }}>{EARN_EMOJIS[i]}</span>
                <div>
                  <strong>{t(`earn.${s}_t` as never)}</strong>
                  <br />
                  {t(`earn.${s}_d` as never)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <Link
          to={"/invite" as never}
          style={{
            display: "block",
            background: "var(--color-sticker-yellow-soft)",
            border: "1px solid var(--color-sticker-yellow)",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 11,
            color: "var(--color-sticker-yellow-fg)",
            textDecoration: "none",
            fontWeight: 700,
          }}
        >
          {t("invite_banner")}
        </Link>
      </div>
    </TgPage>
  );
}
